/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../Modal/Modal';
import { DangerButton, GhostButton } from '../../styles/ui/Button';
import { Field, RequiredMark } from '../../styles/forms/Field';

// ---------------------------------------------------------------------------
// Confirmação destrutiva COM MOTIVO obrigatório — primitivo compartilhado.
//
// Extraído do DeleteMovementModal (handoff 0095) quando a exclusão de anexo da
// régua de cobrança (0096) pediu o mesmo molde: FC7 cap.2 H.4 manda promover o
// padrão ao ser reusado ("extrair, não copiar"). O DeleteMovementModal virou um
// wrapper fino deste componente — a microcópia dele não mudou.
//
// A validação aqui ESPELHA a do backend (o 422 é rede de segurança, não a
// experiência): o mínimo/máximo vêm por prop porque cada domínio tem o seu
// (movimento: 8–255; anexo de tratativa: 3–255).
//
// Botões seguem G.1: confirmar destruição = DangerButton vermelho; voltar =
// ghost. O submit e o tratamento de erro ficam na página — o modal permanece
// aberto em falha (quem fecha é o `isOpen` do pai).
// ---------------------------------------------------------------------------

// $tone="danger" (opt-in, 2026-09-10): faixa vermelha para quando o texto
// descreve o EFEITO destrutivo (ex.: cancelamento que devolve estoque).
// Sem a prop, nada muda para os consumidores existentes.
const InfoText = styled.p`
   margin: 0 0 14px;
   font-size: 13.5px;
   line-height: 1.5;
   color: ${({ $tone }) =>
      $tone === 'danger' ? '#8e1b1b' : 'rgba(0, 0, 0, 0.75)'};
   ${({ $tone }) =>
      $tone === 'danger'
         ? `
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid rgba(220, 38, 38, 0.22);
   background: rgba(220, 38, 38, 0.06);
   font-size: 12.5px;
   `
         : ''}
`;

const FieldFootRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 10px;
`;

const ErrorText = styled.small`
   font-size: 12px;
   font-weight: 600;
   color: ${({ theme }) => theme.colors.danger};
`;

const CounterText = styled.small`
   margin-left: auto;
   font-size: 11.5px;
   color: rgba(0, 0, 0, 0.45);
`;

export default function ReasonConfirmModal({
   isOpen,
   title,
   subtitle = null,
   infoText = null,
   infoTone = null,
   reasonLabel = 'Motivo',
   placeholder = '',
   minLength = 3,
   maxLength = 255,
   confirmLabel = 'Confirmar',
   submittingLabel = 'Enviando…',
   cancelLabel = 'Voltar',
   isSubmitting = false,
   inputId = 'reason-confirm-input',
   maxWidth = '560px',
   onCancel,
   onConfirm,
}) {
   const [reason, setReason] = useState('');
   const [touched, setTouched] = useState(false);

   // Reabrir o modal sempre começa limpo.
   useEffect(() => {
      if (isOpen) {
         setReason('');
         setTouched(false);
      }
   }, [isOpen]);

   const trimmed = reason.trim();
   const isValid = trimmed.length >= minLength && trimmed.length <= maxLength;

   let validationMessage = null;
   if (trimmed.length < minLength) {
      validationMessage = `Informe um motivo com pelo menos ${minLength} caracteres.`;
   } else if (trimmed.length > maxLength) {
      validationMessage = `O motivo pode ter no máximo ${maxLength} caracteres.`;
   }

   return (
      <Modal
         isOpen={isOpen}
         title={title}
         subtitle={subtitle}
         onClose={onCancel}
         footer={
            <>
               <GhostButton
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
               >
                  {cancelLabel}
               </GhostButton>
               <DangerButton
                  type="button"
                  onClick={() => onConfirm(trimmed)}
                  disabled={!isValid || isSubmitting}
               >
                  {isSubmitting ? submittingLabel : confirmLabel}
               </DangerButton>
            </>
         }
         maxWidth={maxWidth}
      >
         {infoText ? <InfoText $tone={infoTone}>{infoText}</InfoText> : null}

         <Field>
            <label htmlFor={inputId}>
               {reasonLabel}
               <RequiredMark>*</RequiredMark>
            </label>
            <textarea
               id={inputId}
               value={reason}
               maxLength={maxLength}
               onChange={(e) => setReason(e.target.value)}
               onBlur={() => setTouched(true)}
               placeholder={placeholder}
               aria-invalid={touched && !isValid}
               disabled={isSubmitting}
            />
            <FieldFootRow>
               {touched && validationMessage ? (
                  <ErrorText>{validationMessage}</ErrorText>
               ) : (
                  <span />
               )}
               <CounterText>
                  {reason.length}/{maxLength}
               </CounterText>
            </FieldFootRow>
         </Field>
      </Modal>
   );
}

ReasonConfirmModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   title: PropTypes.string.isRequired,
   subtitle: PropTypes.node,
   infoText: PropTypes.node,
   infoTone: PropTypes.oneOf(['danger']),
   reasonLabel: PropTypes.string,
   placeholder: PropTypes.string,
   minLength: PropTypes.number,
   maxLength: PropTypes.number,
   confirmLabel: PropTypes.string,
   submittingLabel: PropTypes.string,
   cancelLabel: PropTypes.string,
   isSubmitting: PropTypes.bool,
   inputId: PropTypes.string,
   maxWidth: PropTypes.string,
   onCancel: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
};
